// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 6258f28a-0e54-4fe9-870a-b6767dbb43b2

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Button, Table, TableHead, TableRow, TableCell,
  TableBody, IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Alert, CircularProgress, Tooltip, Avatar, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { getConfig } from './config';
import { getIdToken, getSession } from './auth';
import { TENANTS } from './seed';

// Customer (tenant) registry — the Optimum master side of the house.
//
// This is how an engagement is OPENED (create a customer, lock its email domain,
// brand it) and how a finished one is CLOSED (archive — never delete). Archiving
// revokes client access and freezes the engagement read-only while Optimum keeps
// full visibility, so the discovery record survives the engagement.
//
// The server re-checks every permission; this UI is convenience, not the
// security boundary.

const NAVY = '#021F4B';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const HEAD = 'sectionHeader.main';

const INDUSTRIES = [
  'Healthcare', 'Academic Medical Center', 'Payer / Insurance', 'Life Sciences',
  'Public Sector', 'Higher Education', 'Other',
];

const BLANK = {
  id: '', name: '', kind: 'client', status: 'active',
  emailDomain: '', industry: 'Healthcare', color: BLUE, logoUrl: '',
  contactName: '', contactEmail: '', contactPhone: '',
  billingName: '', billingEmail: '', billingPhone: '',
  addressLine1: '', addressLine2: '', city: '', state: '', postalCode: '',
  sharepointUrl: '', confluenceUrl: '', notes: '',
};

// 'Northeast Georgia Health System' -> 'northeast-georgia-health-system'
export function slugify(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

// The API is being built in parallel and may answer in snake_case (it mirrors
// the `tenant` table) or camelCase. Accept either.
function normalize(row) {
  const r = row || {};
  return {
    ...BLANK,
    ...r,
    id: r.id || '',
    name: r.name || '',
    kind: r.kind || 'client',
    status: r.status || 'active',
    emailDomain: r.emailDomain || r.email_domain || '',
    logoUrl: r.logoUrl || r.logo_url || r.logo || '',
    color: r.color || BLUE,
    industry: r.industry || 'Healthcare',
    sharepointUrl: r.sharepointUrl || r.sharepoint_url || '',
    confluenceUrl: r.confluenceUrl || r.confluence_url || '',
    createdAt: r.createdAt || r.created_at || '',
    archivedAt: r.archivedAt || r.archived_at || '',
  };
}

// Emit both spellings so whichever shape the handler lands on, it gets the data.
function toPayload(c) {
  return {
    ...c,
    email_domain: c.emailDomain,
    logo_url: c.logoUrl,
    sharepoint_url: c.sharepointUrl,
    confluence_url: c.confluenceUrl,
  };
}

function fmtDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  return isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
}

export default function CustomerAdmin({ onChanged }) {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [offline, setOffline] = useState(false); // API not deployed -> seed fallback
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [dialog, setDialog] = useState(null); // { mode:'create'|'edit', customer, idTouched }
  const [confirm, setConfirm] = useState(null); // { customer, archived }
  const [busy, setBusy] = useState(false);

  const me = getSession();
  const iAmMaster = !!me && (me.groups || []).includes('OptimumAdmins');

  const api = useCallback(async (path, options = {}) => {
    const res = await fetch(`${getConfig().apiUrl}${path}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getIdToken()}`, ...(options.headers || {}) },
    });
    const text = await res.text();
    // An error envelope is often an HTML page, not JSON. Parsing it bare threw
    // SyntaxError here, before the !res.ok check below could report the real
    // status, so a 502 surfaced to the user as "Unexpected token <".
    let data = {};
    let parseFailed = false;
    try { data = text ? JSON.parse(text) : {}; } catch (e) { parseFailed = true; }
    if (!res.ok) throw new Error(data.error || data.message || `request failed (${res.status})`);
    if (parseFailed) throw new Error(`The API returned ${res.status} with a body that is not JSON.`);
    return data;
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const data = await api('/customers');
      setCustomers((Array.isArray(data) ? data : data.items || []).map(normalize));
      setOffline(false);
    } catch (e) {
      // Read-only fallback so the screen is useful before the API ships.
      setCustomers(TENANTS.map(normalize));
      setOffline(true);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { if (iAmMaster) load(); else setLoading(false); }, [load, iAmMaster]);

  const readOnly = offline;

  const sorted = useMemo(() => {
    const rank = (c) => (c.status === 'archived' ? 1 : 0);
    return [...customers].sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
  }, [customers]);

  const setC = (patch) => setDialog((d) => ({ ...d, customer: { ...d.customer, ...patch } }));

  const changeName = (name) => {
    setDialog((d) => ({
      ...d,
      customer: {
        ...d.customer,
        name,
        id: d.mode === 'create' && !d.idTouched ? slugify(name) : d.customer.id,
      },
    }));
  };

  // File upload is deliberately absent: /customers/logo is not deployed, so the
  // old handler only ever base64'd the image into state and let submit() persist
  // a multi-megabyte data URI as logo_url. Primordia stores a link, not a file —
  // the Logo URL field is the whole feature until an image store exists.
  const submit = async () => {
    const c = dialog.customer;
    setBusy(true); setErr(''); setMsg('');
    try {
      if (dialog.mode === 'create') {
        await api('/customers', { method: 'POST', body: JSON.stringify(toPayload(c)) });
        setMsg(`Created ${c.name}. Invitations are now limited to @${c.emailDomain || 'their domain'}.`);
      } else {
        await api('/customers', { method: 'PUT', body: JSON.stringify(toPayload(c)) });
        setMsg(`Updated ${c.name}.`);
      }
      setDialog(null);
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const applyArchive = async () => {
    const { customer, archived } = confirm;
    setBusy(true); setErr(''); setMsg('');
    try {
      await api('/customers/archive', { method: 'POST', body: JSON.stringify({ id: customer.id, archived }) });
      setMsg(archived
        ? `${customer.name} archived. Client access is revoked and the engagement is read-only.`
        : `${customer.name} unarchived. Client access is restored.`);
      setConfirm(null);
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!iAmMaster) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">
          Company management is Optimum-only. Ask an Optimum administrator to open, edit, or
          archive a client engagement.
        </Alert>
      </Box>
    );
  }

  const d = dialog;

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: NAVY }}>Companies</Typography>
        <Chip size="small" label="Optimum master registry" sx={{ bgcolor: NAVY, color: '#fff', height: 20 }} />
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={load} title="Refresh"><RefreshIcon /></IconButton>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          disabled={readOnly}
          sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}
          onClick={() => setDialog({ mode: 'create', idTouched: false, customer: { ...BLANK } })}
        >
          New company
        </Button>
      </Stack>

      {offline && (
        <Alert severity="info" sx={{ mb: 2 }}>
          The company API is not yet deployed — showing the built-in tenant list in read-only mode.
          Creating, editing, and archiving become available once <code>/customers</code> is live.
        </Alert>
      )}
      {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert>}
      {msg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg('')}>{msg}</Alert>}

      <Paper>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={26} /></Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, color: HEAD } }}>
                <TableCell sx={{ width: 56 }}>Logo</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>ID</TableCell>
                <TableCell>Email domain</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((c) => (
                <TableRow key={c.id} hover sx={{ opacity: c.status === 'archived' ? 0.65 : 1 }}>
                  <TableCell>
                    <Avatar
                      src={c.logoUrl || undefined}
                      variant="rounded"
                      sx={{ width: 32, height: 32, bgcolor: c.color || BLUE, fontSize: 14, fontWeight: 700 }}
                    >
                      {(c.name || '?').charAt(0).toUpperCase()}
                    </Avatar>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {c.name}
                    {c.kind === 'master' && (
                      <Chip size="small" label="MASTER" sx={{ ml: 1, bgcolor: NAVY, color: '#fff', height: 18 }} />
                    )}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', color: GREY }}>{c.id}</TableCell>
                  <TableCell sx={{ color: GREY }}>{c.emailDomain || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={c.status === 'archived' ? 'archived' : 'active'}
                      color={c.status === 'archived' ? 'default' : 'success'}
                      sx={{ height: 20 }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: GREY }}>{fmtDate(c.createdAt)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title={readOnly ? 'Available once the company API is deployed' : 'Edit company'}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={readOnly}
                          onClick={() => setDialog({ mode: 'edit', idTouched: true, customer: { ...c } })}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    {c.status === 'archived' ? (
                      <Tooltip title={readOnly ? 'Available once the company API is deployed' : 'Unarchive — restore client access'}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={readOnly}
                            onClick={() => setConfirm({ customer: c, archived: false })}
                          >
                            <UnarchiveIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : (
                      <Tooltip title={readOnly ? 'Available once the company API is deployed' : 'Archive — close the engagement'}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={readOnly || c.kind === 'master'}
                            onClick={() => setConfirm({ customer: c, archived: true })}
                          >
                            <ArchiveIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!sorted.length && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ color: '#9AA6B2', textAlign: 'center', py: 3 }}>
                    No companies yet. Open the first engagement with “New company”.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: GREY }}>
        Companies are archived, never deleted — the discovery record has to outlive the engagement.
      </Typography>

      {/* ---------------- create / edit ---------------- */}
      <Dialog open={!!dialog} onClose={() => !busy && setDialog(null)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 700, color: NAVY }}>
          {d && d.mode === 'create' ? 'New company' : 'Edit company'}
        </DialogTitle>
        {d && (
          <DialogContent dividers>
            <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700, mb: 1.5 }}>Company</Typography>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <TextField
                fullWidth size="small" label="Company name" value={d.customer.name}
                onChange={(e) => changeName(e.target.value)}
              />
              <TextField
                fullWidth size="small" label="Short ID" value={d.customer.id}
                disabled={d.mode === 'edit'}
                onChange={(e) => setDialog((prev) => ({
                  ...prev, idTouched: true, customer: { ...prev.customer, id: slugify(e.target.value) },
                }))}
                helperText={d.mode === 'edit' ? 'Permanent — cannot be changed.' : 'lowercase letters, numbers and dashes'}
              />
            </Stack>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <TextField
                fullWidth size="small" label="Email domain" value={d.customer.emailDomain}
                onChange={(e) => setC({ emailDomain: e.target.value.trim().toLowerCase() })}
                helperText="Only addresses at this domain may be invited (e.g. nghs.com)"
              />
              <TextField
                select fullWidth size="small" label="Industry" value={d.customer.industry || 'Healthcare'}
                onChange={(e) => setC({ industry: e.target.value })}
              >
                {INDUSTRIES.map((i) => <MenuItem key={i} value={i}>{i}</MenuItem>)}
              </TextField>
            </Stack>

            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <TextField
                size="small" type="color" label="Brand colour" InputLabelProps={{ shrink: true }}
                sx={{ width: 140 }} value={d.customer.color || BLUE}
                onChange={(e) => setC({ color: e.target.value })}
              />
              <Avatar
                src={d.customer.logoUrl || undefined} variant="rounded"
                sx={{ width: 48, height: 48, bgcolor: d.customer.color || BLUE, fontWeight: 700 }}
              >
                {(d.customer.name || '?').charAt(0).toUpperCase()}
              </Avatar>
              <Tooltip title="Primordia has no image store, so it cannot hold an uploaded file. Host the logo where your users can reach it — SharePoint, a CDN, the client's own site — and paste that link into Logo URL.">
                <span>
                  <Button
                    size="small" variant="outlined" disabled
                    startIcon={<CloudUploadIcon />}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Upload logo
                  </Button>
                </span>
              </Tooltip>
              <TextField
                fullWidth size="small" label="Logo URL" value={d.customer.logoUrl || ''}
                onChange={(e) => setC({ logoUrl: e.target.value })}
                helperText="Link to an image that is already hosted somewhere. The link is stored; the image itself is not."
              />
            </Stack>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700, mb: 1.5 }}>Primary contact</Typography>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <TextField fullWidth size="small" label="Name" value={d.customer.contactName || ''} onChange={(e) => setC({ contactName: e.target.value })} />
              <TextField fullWidth size="small" label="Email" value={d.customer.contactEmail || ''} onChange={(e) => setC({ contactEmail: e.target.value })} />
              <TextField fullWidth size="small" label="Phone" value={d.customer.contactPhone || ''} onChange={(e) => setC({ contactPhone: e.target.value })} />
            </Stack>

            <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700, mb: 1.5 }}>Billing contact</Typography>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <TextField fullWidth size="small" label="Name" value={d.customer.billingName || ''} onChange={(e) => setC({ billingName: e.target.value })} />
              <TextField fullWidth size="small" label="Email" value={d.customer.billingEmail || ''} onChange={(e) => setC({ billingEmail: e.target.value })} />
              <TextField fullWidth size="small" label="Phone" value={d.customer.billingPhone || ''} onChange={(e) => setC({ billingPhone: e.target.value })} />
            </Stack>

            <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700, mb: 1.5 }}>Address</Typography>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <TextField fullWidth size="small" label="Address line 1" value={d.customer.addressLine1 || ''} onChange={(e) => setC({ addressLine1: e.target.value })} />
              <TextField fullWidth size="small" label="Address line 2" value={d.customer.addressLine2 || ''} onChange={(e) => setC({ addressLine2: e.target.value })} />
            </Stack>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <TextField fullWidth size="small" label="City" value={d.customer.city || ''} onChange={(e) => setC({ city: e.target.value })} />
              <TextField fullWidth size="small" label="State" value={d.customer.state || ''} onChange={(e) => setC({ state: e.target.value })} />
              <TextField fullWidth size="small" label="ZIP / postal code" value={d.customer.postalCode || ''} onChange={(e) => setC({ postalCode: e.target.value })} />
            </Stack>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700, mb: 1.5 }}>Integrations</Typography>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <TextField
                fullWidth size="small" label="SharePoint site URL" value={d.customer.sharepointUrl || ''}
                onChange={(e) => setC({ sharepointUrl: e.target.value })}
                helperText="Link-back target for engagement documents"
              />
              <TextField
                fullWidth size="small" label="Confluence space URL" value={d.customer.confluenceUrl || ''}
                onChange={(e) => setC({ confluenceUrl: e.target.value })}
                helperText="Link-back target for discovery pages"
              />
            </Stack>
            <TextField
              fullWidth size="small" multiline minRows={2} label="Notes (internal)"
              value={d.customer.notes || ''} onChange={(e) => setC({ notes: e.target.value })}
            />
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setDialog(null)} disabled={busy}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submit}
            disabled={busy || !d || !d.customer.name || !d.customer.id}
            sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}
          >
            {busy ? 'Saving…' : (d && d.mode === 'create' ? 'Create customer' : 'Save changes')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- archive / unarchive confirm ---------------- */}
      <Dialog open={!!confirm} onClose={() => !busy && setConfirm(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700, color: NAVY }}>
          {confirm && confirm.archived ? 'Archive customer' : 'Unarchive customer'}
        </DialogTitle>
        {confirm && (
          <DialogContent dividers>
            {confirm.archived ? (
              <>
                <Typography sx={{ mb: 2 }}>
                  Archive <b>{confirm.customer.name}</b> and close the engagement?
                </Typography>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Archiving revokes client access immediately and freezes the engagement read-only.
                  Optimum staff keep full visibility of the record — nothing is deleted, and the
                  customer can be unarchived later.
                </Alert>
                <Typography variant="body2" sx={{ color: GREY }}>
                  Users at @{confirm.customer.emailDomain || 'this domain'} will no longer be able to
                  sign in to this workspace.
                </Typography>
              </>
            ) : (
              <>
                <Typography sx={{ mb: 2 }}>
                  Unarchive <b>{confirm.customer.name}</b> and reopen the engagement?
                </Typography>
                <Alert severity="info">
                  Client access is restored and the engagement becomes editable again.
                </Alert>
              </>
            )}
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={busy}>Cancel</Button>
          <Button
            variant="contained"
            onClick={applyArchive}
            disabled={busy}
            sx={{ bgcolor: confirm && confirm.archived ? NAVY : BLUE, '&:hover': { bgcolor: NAVY } }}
          >
            {busy ? 'Working…' : (confirm && confirm.archived ? 'Archive customer' : 'Unarchive customer')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
